import { createContext, useCallback, useContext, useMemo } from 'react';
import { useAcceptAIFields, useDismissAIFields } from '../../../../../services/hooks/captable';

export type AIFieldAcceptContextType = {
  acceptField: (securityId: number, fieldName: string, reason?: string) => Promise<void>;
  acceptAllFields: (securityId: number) => Promise<void>;
  dismissField: (securityId: number, fieldName: string, reason?: string) => Promise<void>;
  dismissAllFields: (securityId: number) => Promise<void>;
};

const AIFieldAcceptContext = createContext<AIFieldAcceptContextType>({
  acceptField: async () => {},
  acceptAllFields: async () => {},
  dismissField: async () => {},
  dismissAllFields: async () => {},
});

export const useAIFieldAcceptContext = () => useContext(AIFieldAcceptContext);

interface AIFieldAcceptProviderProps {
  children: React.ReactNode;
  onDismissAiColumn?: (securityId: number) => void;
}

export const AIFieldAcceptProvider = ({ children, onDismissAiColumn }: AIFieldAcceptProviderProps) => {
  const { acceptAIFields } = useAcceptAIFields();
  const { dismissAIFields } = useDismissAIFields();

  const acceptField = useCallback(
    // `reason` is the optional reviewer note from a single review card; included
    // only when truthy so the default one-click accept keeps a minimal payload.
    async (securityId: number, fieldName: string, reason?: string) => {
      await acceptAIFields(securityId, { field_names: [fieldName], ...(reason ? { reason } : {}) });
    },
    [acceptAIFields]
  );

  const acceptAllFields = useCallback(
    async (securityId: number) => {
      await acceptAIFields(securityId, { all: true });
    },
    [acceptAIFields]
  );

  const dismissField = useCallback(
    async (securityId: number, fieldName: string, reason?: string) => {
      await dismissAIFields(securityId, { field_names: [fieldName], ...(reason ? { reason } : {}) });
      // The `name` field IS an AI-proposed security column. Declining it removes the
      // whole column (onDismissAiColumn -> deleteColumn) rather than moving the card
      // to the Reviewed section: a rejected proposed-security should disappear, not
      // linger. This is intentional and NOT a move-not-remove bug.
      if (fieldName === 'name') {
        onDismissAiColumn?.(securityId);
      }
    },
    [dismissAIFields, onDismissAiColumn]
  );

  const dismissAllFields = useCallback(
    async (securityId: number) => {
      await dismissAIFields(securityId, { all: true });
      onDismissAiColumn?.(securityId);
    },
    [dismissAIFields, onDismissAiColumn]
  );

  const aiFieldAcceptContextValue: AIFieldAcceptContextType = useMemo(() => ({ acceptField, acceptAllFields, dismissField, dismissAllFields }), [acceptField, acceptAllFields, dismissField, dismissAllFields]);

  return (
    <AIFieldAcceptContext.Provider value={aiFieldAcceptContextValue}>
      {children}
    </AIFieldAcceptContext.Provider>
  );
};

export default AIFieldAcceptContext;
